// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for creating new asset project assignments.
 * This module provides a comprehensive interface for assigning
 * assets to projects with date range selection, timeline
 * visualization, and validation. It ensures assignments maintain
 * chronological order and prevents overlaps.
 *
 * Key features:
 * - Project selection
 * - Date range validation
 * - Timeline visualization
 * - Error handling
 * - Real-time updates
 */
import { hasRangeViolationError } from '@/api/error-utils';
import { assignAsset } from '@/asset/api/assign-asset';
import { getAssetProjectHistory } from '@/asset/api/get-asset-project-history';
import { PartialAsset } from '@/asset/api/types';
import { AssetInventoryDateWarning } from '@/asset/components/asset-inventory-date-warning';
import {
  AssetProjectHistoryTimeline,
  HISTORY_SEGMENT_INPUT_ID,
} from '@/asset/components/asset-project-history-timeline';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { projectStatusSchema } from '@/constants/project-status';
import { FormProjectSearch } from '@/project/components/form-project-search';
import { sparseProjectSchema } from '@/sparse/api/types';
import { normalizeDateTime } from '@/utilities/date-time';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Calculates a valid interval from start and end dates.
 * Returns null if dates are invalid or form an invalid range.
 *
 * @param start - The start date
 * @param end - The end date
 * @returns A Luxon interval or null if invalid
 */
function getInterval(start: DateTime | null, end: DateTime | null) {
  if (
    start === null ||
    end === null ||
    !DateTime.isDateTime(start) ||
    !DateTime.isDateTime(end) ||
    !start.isValid ||
    !end.isValid
  ) {
    return null;
  }

  if (start > end) {
    return null;
  }

  return start.until(end);
}

/**
 * Calculates the visible interval for timeline display.
 * Adds padding days before and after the assignment period
 * to provide context in the timeline visualization.
 *
 * @param start - The start date
 * @param end - The end date
 * @returns A Luxon interval for timeline display
 */
function getTimelineInterval(start: DateTime | null, end: DateTime | null) {
  const today = DateTime.now().endOf('day');
  const interval = getInterval(start ?? today, end ?? today);

  if (interval === null) {
    return today.minus({ days: 7 }).until(today.plus({ days: 7 }));
  }

  const length = interval.length('days');
  const days = Math.max(length, 7);

  return interval.set({
    start: interval.start.minus({ days }),
    end: DateTime.max(
      interval.start.minus({ days }),
      DateTime.min(
        interval.end.plus({ days }),
        DateTime.now().endOf('day').plus({ days: 7 })
      )
    ),
  });
}

/**
 * Schema for validating form input data.
 * Enforces required fields, validates date ranges, and ensures dates are not before January 1, 1970.
 */
const formSchema = z
  .object({
    project: sparseProjectSchema.nullable(),
    start: dateSchema.nullable(),
    end: dateSchema.nullable(),
  })
  .superRefine((data, context) => {
    if (data.project === null) {
      context.addIssue({
        path: ['project'],
        code: z.ZodIssueCode.custom,
        message: 'Project is required',
      });
    }

    if (data.start === null) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: 'Start date is required',
      });
    }

    if (data.end === null) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'End date is required',
      });
    }

    if (data.start === null || data.end === null) {
      return;
    }

    if (data.start < MINIMUM_DATE) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: 'Start date cannot be before January 1, 1970',
      });
    }

    if (data.end < MINIMUM_DATE) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'End date cannot be before January 1, 1970',
      });
    }

    const today = DateTime.now().endOf('day');

    if (data.start > data.end) {
      return context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
      });
    }

    if (data.start > today) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be assigned in the future',
      });
    }

    if (data.end > today) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be assigned in the future',
      });
    }
  });

/**
 * Type definition for form data.
 * Used for both input and output validation.
 *
 * @typedef {Object} FormData
 */
type FormData = z.infer<typeof formSchema>;

/**
 * Props for configuring the CreateAssetAssignmentDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to assign to a project.
   * Only requires id, key, serial and inventory date fields.
   */
  asset: PartialAsset;

  /**
   * Optional default start date for the assignment.
   */
  defaultStartDate?: DateTime;

  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for creating new asset project assignments.
 * Provides a form interface for selecting a project and date range
 * with timeline visualization and validation.
 *
 * Features:
 * - Project search and selection
 * - Date range validation
 * - Timeline visualization
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ asset }: Props) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <CreateAssetAssignmentDialog
 *       asset={asset}
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function CreateAssetAssignmentDialog(props: Props) {
  const queryClient = useQueryClient();

  const { control, reset, handleSubmit, setError } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    values: {
      start: props.defaultStartDate ?? null,
      end: null,
      project: null,
    },
  });

  const historyQuery = useQuery({
    queryKey: ['asset', props.asset.key, 'project-history'],
    queryFn: () => getAssetProjectHistory(props.asset.key),
  });

  const mutation = useMutation({
    mutationFn: (data: FormData) => {
      if (data.project === null) {
        throw new Error('A project is required', { cause: data });
      }

      if (data.start === null) {
        throw new Error('A start date is required', { cause: data });
      }

      if (data.end === null) {
        throw new Error('An end date is required', { cause: data });
      }

      return assignAsset(props.asset.id, data.project.key, {
        start: normalizeDateTime(data.start),
        end: normalizeDateTime(data.end),
      });
    },
    onSuccess: (data, variables) => {
      close();

      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: ['asset', props.asset.key, 'project-history'],
        });

        if (variables.project !== null) {
          queryClient.invalidateQueries({
            queryKey: ['project', variables.project.key],
          });
        }
      }
    },
    onError: (error) => {
      if (hasRangeViolationError(error)) {
        setError('start', {
          type: 'manual',
          message:
            'An asset assignment already intersects with this date range.',
        });
        setError('end', {
          type: 'manual',
          message:
            'Please adjust the date range or correct the dates for the existing assignment.',
        });
      }
    },
  });

  const project = useWatch({ control, name: 'project' });
  const start = useWatch({ control, name: 'start' });
  const end = useWatch({ control, name: 'end' });

  const startDateTime = start;
  const endDateTime = end;
  const today = DateTime.now().endOf('day');
  const history = historyQuery.data?.history ?? [];
  const interval = getInterval(start, end);
  const intervals =
    history
      .map(({ start, end }) => {
        const endDate = end ?? today;
        if (start > endDate) {
          return null;
        }
        return start.until(endDate);
      })
      .filter(
        (interval): interval is NonNullable<typeof interval> =>
          interval !== null
      ) ?? [];

  const timelineInterval = getTimelineInterval(start, end);

  const timelineIndex = history.findIndex((segment) => {
    if (start === null) {
      return false;
    }

    if (segment.end === null) {
      return segment.start < start;
    }

    return segment.end < start;
  });

  const timelineHistory =
    start === null
      ? history
      : [
          ...history.slice(0, timelineIndex),
          {
            id: HISTORY_SEGMENT_INPUT_ID,
            key: HISTORY_SEGMENT_INPUT_ID,
            start,
            end,
            project: {
              id: project?.id ?? HISTORY_SEGMENT_INPUT_ID,
              key: project?.key ?? HISTORY_SEGMENT_INPUT_ID,
              name: project?.name ?? 'New assignment',
              startOn: null,
              endOn: null,
              isArchived: false,
              status: projectStatusSchema.Values.DATE_RANGE_INCOMPLETE,
              customer: project?.customer ?? null,
              integrations: project?.integrations ?? [],
              paymentTermLabel: null,
              purchaseOrderNumber: null,
            },
          },
          ...history.slice(timelineIndex),
        ];

  const isInvalid = intervals.some((other) => {
    if (interval === null) {
      return false;
    }

    return other.overlaps(interval);
  });

  /**
   * Handles form submission when validation passes.
   * Creates the asset assignment and updates queries.
   *
   * @param data - The validated form data
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (isInvalid) {
      return;
    }

    mutation.mutate(data);
  };

  /**
   * Closes the dialog and resets form state.
   * Cleans up mutation state and form values.
   */
  const close = () => {
    if (mutation.isPending) return;

    props.onClose();

    reset({
      start: props.defaultStartDate ?? null,
      end: null,
      project: null,
    });
  };

  const showGenericError =
    mutation.isError && !hasRangeViolationError(mutation.error);

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={showGenericError}
        onClose={mutation.reset}
        message="An error occurred while creating the assignment."
      />
      <DialogTitle>Asset assignment</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack>
            <Typography variant="subtitle1">Asset</Typography>
            <Typography>{props.asset.serial}</Typography>
          </Stack>
          <FormProjectSearch
            name="project"
            label="Project"
            control={control}
            disabled={mutation.isPending}
          />
          <Stack direction="row" alignItems="center">
            <FormDatePicker
              disableFuture
              name="start"
              label="Start date"
              control={control}
              disabled={mutation.isPending}
            />
            <AssetInventoryDateWarning
              asset={props.asset}
              dateTime={startDateTime}
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormDatePicker
              disableFuture
              name="end"
              label="End date"
              control={control}
              disabled={mutation.isPending}
            />
            <AssetInventoryDateWarning
              asset={props.asset}
              dateTime={endDateTime}
            />
          </Stack>
        </Stack>
        <Collapse in={isInvalid}>
          <Alert severity="error" sx={{ marginTop: 2 }}>
            This assignment overlaps with another assignment for this asset.
            Please adjust the date range or correct the dates for the existing
            assignment.
          </Alert>
        </Collapse>
      </DialogContent>
      <AssetProjectHistoryTimeline
        asset={props.asset}
        history={timelineHistory}
        interval={timelineInterval}
        minimumHeight={50}
        maximumHeight={100}
      />
      <Divider />
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          disabled={mutation.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
